import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '../utils/matomoEvents';

// One entry per condition. The id is the #anchor a puppy card's "Learn more"
// link points at, so keep it stable once a litter links to it. To cover
// another condition, add an entry here.
const healthTopics = [
  {
    id: 'inguinal-hernia',
    title: 'Inguinal Hernia',
    sections: [
      {
        heading: 'What it is',
        body: 'A small opening in the muscle wall of the groin, where the hind leg meets the belly. It shows up as a soft bump in that spot. In puppies it is usually there from birth.'
      },
      {
        heading: 'How it is handled',
        body: 'Small inguinal hernias are a common, well-understood finding and are usually caught at a routine vet check. Because this kind of opening rarely closes on its own, vets typically recommend a simple surgery to close it. Many owners have it done at the same time as the spay or neuter, so the puppy only goes under anesthesia once. When one of our puppies has a small inguinal hernia, we lower the price to cover the cost of the repair.'
      }
    ]
  },
  {
    id: 'umbilical-hernia',
    title: 'Umbilical Hernia',
    sections: [
      {
        heading: 'What it is',
        body: 'A small opening at the belly button where the belly wall did not fully close after birth. It looks like a soft, squishy bump on the belly that can often be gently pushed back in.'
      },
      {
        heading: 'How it is handled',
        body: 'This is one of the most common things vets find in puppies of any breed. Very small umbilical hernias often close on their own as the puppy grows, usually by about six months old. If one is still open by then, your vet can close it with a quick procedure, often during the spay or neuter.'
      }
    ]
  }
];

export default function HealthInfo() {
  const { hash } = useLocation();

  // A puppy card links here with a #<topic> hash. A router navigation does not
  // scroll on its own, so do it here. `.health-topic` carries a
  // scroll-margin-top to clear the fixed header.
  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(hash.slice(1));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      trackEvent('Health', 'ArriveAtTopic', hash.slice(1));
    }
  }, [hash]);

  return (
    <div className="health-info">
      <header className="health-intro">
        <h2>French Bulldog Health</h2>
        <p>
          Every breed has a few health conditions worth knowing about, and
          Frenchies are no exception. This page explains the ones you may see
          mentioned on our puppy listings, in plain language, so you know
          exactly what you are getting.
        </p>
      </header>

      <section className="health-commitment">
        <h3>Our Commitment</h3>
        <p>
          We will never sell a puppy with a serious or life-threatening
          condition. If a puppy has a minor issue, we tell you up front. Any
          puppy listed at a reduced price for a health reason has been examined
          and cleared by our veterinarian, and the reduction is meant to cover
          the cost of any recommended treatment.
        </p>
      </section>

      {healthTopics.map(topic => (
        <section key={topic.id} id={topic.id} className="health-topic">
          <h3>{topic.title}</h3>
          {topic.sections.map(section => (
            <div key={section.heading} className="health-topic-part">
              <h4>{section.heading}</h4>
              <p>{section.body}</p>
            </div>
          ))}
        </section>
      ))}

      <section className="health-topic">
        <h3>When to Call Your Vet</h3>
        <p>
          A small hernia is easy to keep an eye on at home. Call your vet if the
          bump becomes hard, painful, red or discolored, gets noticeably bigger,
          or your puppy is vomiting or stops eating their food. This is uncommon with a
          small hernia, but it is good to know what to look for.
        </p>
      </section>

      <p className="health-disclaimer">
        This page is general information, not veterinary advice. Your own vet
        knows your puppy best. Questions about a specific puppy? Call us at{' '}
        <a href="tel:3373522160">(337) 352-2160</a>.
      </p>

      <style jsx>{`
        .health-info {
          max-width: 860px;
          margin: 0 auto;
          padding: 2rem;
        }
        .health-intro {
          text-align: center;
          margin-bottom: 2rem;
        }
        .health-intro h2 {
          color: var(--heading);
          margin-bottom: 1rem;
        }
        .health-intro p {
          color: var(--text-muted);
          font-size: 1.1rem;
          line-height: 1.6;
        }
        .health-commitment,
        .health-topic {
          scroll-margin-top: 110px;
          background: var(--surface-sunken);
          border-radius: 8px;
          padding: 1.5rem 2rem;
          margin-bottom: 1.5rem;
        }
        .health-commitment {
          border-left: 4px solid var(--success);
        }
        .health-commitment h3,
        .health-topic h3 {
          margin: 0 0 0.75rem 0;
          color: var(--heading);
        }
        .health-topic-part + .health-topic-part {
          margin-top: 1rem;
        }
        .health-topic h4 {
          margin: 0 0 0.35rem 0;
          color: var(--heading);
        }
        .health-commitment p,
        .health-topic p {
          margin: 0;
          line-height: 1.6;
        }
        .health-disclaimer {
          text-align: center;
          color: var(--text-faint);
          font-size: 0.95rem;
          line-height: 1.6;
        }
        .health-disclaimer a {
          color: var(--accent);
          text-decoration: none;
        }
        @media (max-width: 768px) {
          .health-info {
            padding: 1rem;
          }
          .health-commitment,
          .health-topic {
            padding: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
}
